import React, { useEffect, useState } from 'react';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Edit, Trash2, Package, BookOpen, RefreshCw } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BOOKSHELF_TYPES = [
  "Infant/Toddler Bookshelf",
  "Standard Size Bookshelf"
];

const BOOK_TYPES = [
  "Board Books",
  "Elementary Age Books",
  "Teen Books"
];

export default function Inventory() {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    category: 'bookshelves',
    item_type: BOOKSHELF_TYPES[0],
    quantity: 0,
    notes: ''
  });

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      const response = await api.get('/inventory');
      setInventory(response.data);
    } catch (error) {
      toast.error('Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  };

  const initializeInventory = async () => {
    try {
      const response = await api.post('/inventory/initialize');
      toast.success(response.data.message);
      fetchInventory();
    } catch (error) {
      toast.error('Failed to initialize inventory');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editItem) {
        await api.put(`/inventory/${editItem.id}`, formData);
        toast.success('Inventory item updated successfully!');
      } else {
        await api.post('/inventory', formData);
        toast.success('Inventory item added successfully!');
      }
      setShowDialog(false);
      setEditItem(null);
      setFormData({ category: 'bookshelves', item_type: BOOKSHELF_TYPES[0], quantity: 0, notes: '' });
      fetchInventory();
    } catch (error) {
      toast.error('Failed to save inventory item');
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      category: item.category,
      item_type: item.item_type,
      quantity: item.quantity,
      notes: item.notes || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await api.delete(`/inventory/${deleteId}`);
      toast.success('Inventory item deleted successfully');
      fetchInventory();
    } catch (error) {
      toast.error('Failed to delete inventory item');
    } finally {
      setDeleteId(null);
    }
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    setEditItem(null);
    setFormData({ category: 'bookshelves', item_type: BOOKSHELF_TYPES[0], quantity: 0, notes: '' });
  };

  const handleCategoryChange = (category) => {
    const defaultType = category === 'bookshelves' ? BOOKSHELF_TYPES[0] : BOOK_TYPES[0];
    setFormData({ ...formData, category, item_type: defaultType });
  };

  // Group inventory by category
  const bookshelfInventory = inventory.filter(item => item.category === 'bookshelves');
  const bookInventory = inventory.filter(item => item.category === 'books');

  const renderInventoryCard = (item) => (
    <Card key={item.id} className="border-stone-200 shadow-card hover:shadow-floating transition-all" data-testid={`inventory-item-${item.id}`}>
      <CardContent className="pt-6">
        <div className="space-y-3">
          <div>
            <p className="font-semibold text-lg">{item.item_type}</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-primary" data-testid={`quantity-${item.id}`}>{item.quantity}</p>
            <p className="text-sm text-stone-600">Available</p>
          </div>
          {item.notes && (
            <div>
              <p className="text-sm text-stone-600">Notes</p>
              <p className="text-sm" data-testid={`notes-${item.id}`}>{item.notes}</p>
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEdit(item)}
              className="flex-1"
              data-testid={`edit-button-${item.id}`}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteId(item.id)}
              data-testid={`delete-button-${item.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="flex">
      <Sidebar />
      <div className="ml-64 flex-1 p-8" data-testid="inventory-page">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">Inventory</h1>
              <p className="text-stone-600">Manage bookshelves and books inventory</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={initializeInventory}
                className="rounded-full"
                data-testid="initialize-inventory-button"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Initialize All Categories
              </Button>
              <Button
                onClick={() => setShowDialog(true)}
                className="rounded-full"
                data-testid="add-inventory-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : inventory.length === 0 ? (
            <Card className="border-stone-200 shadow-card">
              <CardContent className="py-12 text-center">
                <p className="text-stone-600 mb-4">No inventory items found</p>
                <Button onClick={initializeInventory} className="rounded-full">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Initialize Inventory Categories
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-8">
              {/* Bookshelves Section */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Package className="w-6 h-6 text-primary" />
                  <h2 className="text-2xl font-bold">Bookshelves</h2>
                </div>
                {bookshelfInventory.length === 0 ? (
                  <Card className="border-stone-200 shadow-card">
                    <CardContent className="py-8 text-center">
                      <p className="text-stone-600">No bookshelf inventory items</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="bookshelf-inventory-list">
                    {bookshelfInventory.map(renderInventoryCard)}
                  </div>
                )}
              </div>

              {/* Books Section */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="w-6 h-6 text-primary" />
                  <h2 className="text-2xl font-bold">Books</h2>
                </div>
                {bookInventory.length === 0 ? (
                  <Card className="border-stone-200 shadow-card">
                    <CardContent className="py-8 text-center">
                      <p className="text-stone-600">No book inventory items</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="book-inventory-list">
                    {bookInventory.map(renderInventoryCard)}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={handleDialogClose}>
        <DialogContent data-testid="inventory-dialog">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit' : 'Add'} Inventory Item</DialogTitle>
            <DialogDescription>
              {editItem ? 'Update' : 'Add'} inventory details below
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="category">Category *</Label>
                <select
                  id="category"
                  data-testid="category-input"
                  value={formData.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                  required
                  disabled={!!editItem}
                >
                  <option value="bookshelves">Bookshelves</option>
                  <option value="books">Books</option>
                </select>
              </div>
              <div>
                <Label htmlFor="item_type">Item Type *</Label>
                <select
                  id="item_type"
                  data-testid="item-type-input"
                  value={formData.item_type}
                  onChange={(e) => setFormData({ ...formData, item_type: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                  required
                  disabled={!!editItem}
                >
                  {(formData.category === 'bookshelves' ? BOOKSHELF_TYPES : BOOK_TYPES).map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="quantity">Quantity *</Label>
                <Input
                  id="quantity"
                  data-testid="quantity-input"
                  type="number"
                  min="0"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  data-testid="notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optional notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleDialogClose} data-testid="dialog-cancel">
                Cancel
              </Button>
              <Button type="submit" className="rounded-full" data-testid="dialog-submit">
                {editItem ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the inventory item.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
